import { useState } from 'react';
import { Button, MenuItem, Stack, TextField } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { AccessDenied } from '@/components/shell/AccessDenied';
import { ApiRequestError } from '@/api/errors';
import { payApiClient } from '@/api/client';
import { EnumMultiSelect } from '@/features/operations/filters/EnumMultiSelect';
import { TagsField } from '@/features/operations/filters/TagsField';
import type { AdminTenantPolicy, AdminTenantPolicyUpdateRequest, OrderReferenceConflictPolicy, PaymentIntentType } from '@/api/models';

interface TenantPolicyFormProps {
  tenantCode: string;
  initial?: AdminTenantPolicy;
  onSaved: (policy: AdminTenantPolicy) => void;
  onCancel: () => void;
}

const TYPE_VALUES: PaymentIntentType[] = ['sale', 'preauth', 'verify', 'terminal'];
const CONFLICT_POLICY_VALUES: OrderReferenceConflictPolicy[] = ['reject', 'return_existing'];
const CURRENCY_PATTERN = /^[A-Za-z]{3}$/;
const RETURN_ORIGIN_PATTERN = /^https:\/\/.+/;

/**
 * `PUT /v1/admin/tenants/{code}/policy` — creates the policy row if none
 * exists yet, or replaces it whole otherwise; used both from the empty
 * state ("Crear política") and the edit action, per the contract's own
 * create-or-replace semantics. `maxAmountMinor` is entered and sent as a
 * plain integer in minor units — never a formatted/decimal round-trip.
 */
export function TenantPolicyForm({ tenantCode, initial, onSaved, onCancel }: TenantPolicyFormProps) {
  const translate = useTranslate();
  const notify = useNotify();
  const [maxAmountMinor, setMaxAmountMinor] = useState(initial?.maxAmountMinor ?? 0);
  const [currencies, setCurrencies] = useState<string[]>(initial?.currencies ?? []);
  const [allowedTypes, setAllowedTypes] = useState<PaymentIntentType[]>(initial?.allowedTypes ?? []);
  const [allowedReturnOrigins, setAllowedReturnOrigins] = useState<string[]>(initial?.allowedReturnOrigins ?? []);
  const [conflictPolicy, setConflictPolicy] = useState<OrderReferenceConflictPolicy>(
    initial?.orderReferenceConflictPolicy ?? 'reject',
  );
  const [accessDenied, setAccessDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    maxAmountMinor >= 0 &&
    currencies.length > 0 &&
    allowedTypes.length > 0 &&
    allowedReturnOrigins.length > 0 &&
    !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const body: AdminTenantPolicyUpdateRequest = {
        maxAmountMinor: Math.trunc(maxAmountMinor),
        currencies,
        allowedTypes,
        allowedReturnOrigins,
        orderReferenceConflictPolicy: conflictPolicy,
      };
      const policy = await payApiClient.updateAdminTenantPolicy(tenantCode, body);
      notify(translate('pay.tenants.policy.success'), { type: 'success' });
      onSaved(policy);
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.errorCode === 'access_denied') {
        setAccessDenied(true);
      } else {
        const message = caught instanceof ApiRequestError ? caught.message : undefined;
        notify(message ?? translate('pay.errorState.body'), { type: 'error' });
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (accessDenied) return <AccessDenied />;

  return (
    <Stack spacing={2.5}>
      <TextField
        type="number"
        size="small"
        label={translate('pay.tenants.policy.fieldMaxAmount')}
        helperText={translate('pay.tenants.policy.fieldMaxAmountHelp')}
        value={maxAmountMinor}
        inputProps={{ min: 0, step: 1 }}
        onChange={(event) => setMaxAmountMinor(Math.max(0, Math.trunc(Number(event.target.value) || 0)))}
        fullWidth
      />
      <TagsField
        label={translate('pay.tenants.policy.fieldCurrencies')}
        helperText={translate('pay.tenants.policy.fieldCurrenciesHelp')}
        values={currencies}
        validate={(value) => CURRENCY_PATTERN.test(value)}
        onChange={(values) => setCurrencies(values.map((value) => value.toUpperCase()))}
      />
      <EnumMultiSelect
        label={translate('pay.tenants.policy.fieldAllowedTypes')}
        options={TYPE_VALUES.map((value) => ({ value, label: translate(`pay.operations.type.${value}`) }))}
        values={allowedTypes}
        onChange={(values) => setAllowedTypes(values as PaymentIntentType[])}
      />
      <TagsField
        label={translate('pay.tenants.policy.fieldAllowedReturnOrigins')}
        helperText={translate('pay.tenants.policy.fieldAllowedReturnOriginsHelp')}
        values={allowedReturnOrigins}
        validate={(value) => RETURN_ORIGIN_PATTERN.test(value)}
        onChange={setAllowedReturnOrigins}
      />
      <TextField
        select
        size="small"
        label={translate('pay.tenants.policy.fieldConflictPolicy')}
        value={conflictPolicy}
        onChange={(event) => setConflictPolicy(event.target.value as OrderReferenceConflictPolicy)}
        fullWidth
      >
        {CONFLICT_POLICY_VALUES.map((value) => (
          <MenuItem key={value} value={value}>
            {translate(`pay.tenants.policy.conflictPolicy.${value}`)}
          </MenuItem>
        ))}
      </TextField>
      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button onClick={onCancel}>{translate('pay.tenants.policy.cancel')}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {translate('pay.tenants.policy.submit')}
        </Button>
      </Stack>
    </Stack>
  );
}
